import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { unzip } from "@repo/utils";
import { CommentRootModel } from "@repo/structure";
import { db, ProposalSocketProvider } from "@client";
import {
  fetchGroupedProjectVersionCommentsQuery,
  fetchProjectProposalQuery,
  getProjectPresetQuery,
  registerCommenterQuery,
} from "@query";
import { sharedProposal } from "@paths";
import { PROJECT_TYPE, HtmlParser, setCustomCompanyFavicon, ShareLinkModifiers, NO_PROPOSAL_ALERTS } from "@utils";
import { useSharedProjectParams } from "@hooks";
import {
  EstimatePresetStore,
  EstimatePresetStoreProvider,
  EstimateEditorStore,
  EstimateEditorStoreProvider,
  EstimateCommentsProvider,
  UnsupportedBrowserAlert,
} from "@tools";
import { Dialog, PageCircularProgress, PdfUploadProgress } from "@components";
import { ProposalView } from "./ProposalView";
import { sendProposalAlert } from "@client/externals/Slack";
import { omit } from "lodash";

export const Proposal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ searchParams ] = useSearchParams();
  const {
    company,
    project,
    projectHash,
    versionKey,
    shareKey
  } = useSharedProjectParams();

  const [ store, setStore ] = useState(null);
  const [ commentStore, setCommentStore ] = useState(null);
  const [ presetStore, setPresetStore ] = useState(null);
  const [ commentHash, setCommentHash ] = useState(null);
  const [ isRemoved, setRemoved ] = useState(false);
  const [ isArchived, setArchived ] = useState(false);
  const [ unsupportedBrowserAlert, setUnsupportedBrowserAlert ] = useState(false);
  const [ proposalHiddenVersions, setHiddenVersions ] = useState([]);
  const [ canCommentOnProposal, setCanComment ] = useState(false);
  
  const clientEmail = useMemo(() => (
    searchParams.get(ShareLinkModifiers.CLIENT_EMAIL) || null
  ), []);
  
  useEffect(() => {
    document.title = t("routes.estimate");
    setCustomCompanyFavicon();
    onLoad();
  }, []);
  
  const onLoad = async () => {
    let proposalName = t("common.unnamed");
    let projectPreset, comment, commentUuid, commenterData, projectData, defaultVersionKey, groupedComments = [];
    
    const noAlerts = searchParams.get(NO_PROPOSAL_ALERTS) || import.meta.env.APP_BLOCK_PROPOSAL_ALERTS;
    
    if (typeof Promise.withResolvers !== "function") {
      setUnsupportedBrowserAlert(true);
      return;
    }
    
    try {
      projectPreset = await getProjectPresetQuery(shareKey);
      const res = await fetchProjectProposalQuery(shareKey, true);
      comment = res.comment
      commentUuid = res.commentUuid
      projectData = omit(res, "comment", "commentUuid");
      
      if(projectData?.name) {
        proposalName = projectData.name;
        document.title = `${projectData.name} - ${t("routes.estimate")}`;
      }

      if (projectData.status === "archive") {
        setArchived(true);
        return;
      }
    } catch (e) {
      console.error(e);
      if(e.reload) {
        console.log("RELOAD:", e.reload);
        location.replace(location.href);
        return;
      }
      if (e?.response?.data?.status === 400) {
        setArchived(true);
        return;
      }
      else if (
        e?.response?.data?.status === 403 ||
        e?.response?.data?.status === 404
      ) {
        setRemoved(true);
        return;
      }
      
      if(!noAlerts)
        await sendProposalAlert(proposalName, "Fetch proposal", e?.response?.data || e)
      console.error(e);
      
      return;
      
      // if(e.versionData) {
      //   await sendProposalAlert(proposalName, e.error, e.versionData)
      //   console.error(e.error);
      // } else {
      //   await sendProposalAlert(proposalName, e)
      //   console.error(e);
      // }
    }
    
    try {
      for(const ver of projectData.versions) {
        let structure = JSON.parse(unzip(ver.structure));
        structure.hideEmptySectionDescriptions = true;
        if(structure.visibility)
          structure.visibility.apply = true;
        ver.structure = structure;
      }
      
      if(versionKey && projectData.versions.find(v => v.key === Number(versionKey))) {
        defaultVersionKey = Number(versionKey);
      }
    } catch(e) {
      if(!noAlerts)
        await sendProposalAlert(proposalName, "Data parsing", e)
      console.error(e);
      
      return;
    }
    
    try {
      const commenter = await db.comment.where({ project: projectHash })?.toArray();
      commenterData = commenter?.[0]
        ? { ...commenter[0], fullName: commenter[0].fullname || commenter[0].fullName }
        : undefined;
      
      if(!commenterData) {
        const d = await registerCommenterQuery(projectHash, "");
        commenterData = {
          uuid: d.uuid,
          color: d.color,
        }
        db.comment.put({
          ...commenterData,
          project: projectHash,
          timestamp: new Date().getTime()
        });
      }
      
      const hiddenVersions = projectData.versions.filter(v => v.visible === 0).map(v => v.key);
      
      groupedComments = comment
        ? await fetchGroupedProjectVersionCommentsQuery(projectHash, commenterData?.uuid)
        : [];
      
      if(groupedComments && hiddenVersions.length > 0)
        groupedComments = groupedComments.filter(group => !hiddenVersions.includes(group.version));
      
      setHiddenVersions(hiddenVersions);
      setCanComment(comment);
    } catch(e) {
      if(!noAlerts)
        await sendProposalAlert(proposalName, "Fetch comments", e)
      console.error(e);
    }
    
    try {
      setCommentHash(commentUuid);
      setPresetStore(new EstimatePresetStore(projectPreset, undefined, true));
      setCommentStore(CommentRootModel.create({
        commenterData,
        groups: groupedComments,
        useInternalComments: false,
        useExternalComments: comment,
      }));
      setStore(new EstimateEditorStore({
        estimateData: projectData,
        estimateType: PROJECT_TYPE.STANDARD,
        unzipped: true,
        isSharedProposal: true,
        defaultVersionKey,
        navigateFunc: (versionKey) => navigate(sharedProposal(company, project, projectHash, versionKey), { replace: true }),
      }));
      
    } catch(e) {
      if(!noAlerts)
        await sendProposalAlert(proposalName, "Rendering", e, e?.versionData)
      console.error(e);
    }
  }
  
  const reloadProposalComments = async () => {
    const commenter = await db.comment.where({ project: projectHash })?.toArray();
    let commenterData = commenter?.[0]
    
    if(canCommentOnProposal && commenterData) {
      let groupedComments = await fetchGroupedProjectVersionCommentsQuery(projectHash, commenterData.uuid)
      if(groupedComments && proposalHiddenVersions.length > 0)
        groupedComments = groupedComments.filter(group => !proposalHiddenVersions.includes(group.version));
      
      commentStore.setGroups(groupedComments);
    }
    
    return commentHash;
  };

  if (isRemoved)
    return (
      <Dialog open>
        <h3>{HtmlParser(t("alerts.editor.removed_share"))}</h3>
      </Dialog>
    );

  if (isArchived)
    return (
      <Dialog open>
        <h3>{HtmlParser(t("alerts.editor.archive_share"))}</h3>
      </Dialog>
    );

  if (unsupportedBrowserAlert)
    return <UnsupportedBrowserAlert variant="proposal" />;

  return !store ? (
    <PageCircularProgress />
  ) : (
    <EstimateCommentsProvider value={commentStore}>
      <EstimateEditorStoreProvider value={store}>
        <EstimatePresetStoreProvider value={presetStore}>
          <ProposalSocketProvider
            commentHash={commentHash}
            reloadProposalComments={reloadProposalComments}
            shareKey={shareKey}
            clientEmail={clientEmail}
          >
            <ProposalView />
            {/*<SessionRecorder commentHash={commentHash} reloadComments={reloadProposalComments} />*/}
            <PdfUploadProgress />
          </ProposalSocketProvider>
        </EstimatePresetStoreProvider>
      </EstimateEditorStoreProvider>
    </EstimateCommentsProvider>
  );
};
